import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { TokenService } from './token.service';
import { UserService } from 'src/app/modules/security/services/user.service';
import { Rol } from '../models/rol/rol';
import {
	SESSION_STORAGE_CURRENT_COMPANY_KEY,
	SESSION_STORAGE_USER_ROLES_KEY
} from 'src/app/shared/constants/session-storage-keys';
import { Router } from '@angular/router';

@Injectable({
	providedIn: 'root'
})
export class AuthService {
	rolInicial: Rol = {
		descripcion: 'Inicial'
	};

	idUser: any;
	isSystemOperator: any;
	private companiesLoaded = false;
	/** Emite antes de cambiar el contexto para cancelar lecturas de la empresa anterior. */
	public readonly companyChanging = new Subject<void>();
	public userCompany = new ReplaySubject<{ id: number; razonSocial: string }>(
		1
	);
	public userCompanies = new ReplaySubject<
		{ id: number; razonSocial: string }[]
	>(1);
	public userRoles: Subject<Rol[]> = new Subject();
	public userRolesInicial: BehaviorSubject<Rol[]> = new BehaviorSubject<Rol[]>(
		this.getUserRoles() != null ? this.getUserRoles()! : [this.rolInicial]
	);

	constructor(
		private msalService: MsalService,
		private tokenService: TokenService,
		private user: UserService,
		private router: Router
	) {}

	private setSessionUserRoles(userRoles: Rol[]): void {
		sessionStorage.setItem(
			SESSION_STORAGE_USER_ROLES_KEY,
			JSON.stringify(userRoles)
		);
	}

	hasPermission(requiredPermission: string): boolean {
		const userRoles = this.getUserRoles();
		if (!userRoles) {
			return false; // Or handle the case where roles are not available
		}

		return userRoles.some(role =>
			role.permisos?.some(
				permission => permission.descripcion === requiredPermission
			)
		);
	}

	getUserRoles(): Rol[] | null {
		const storedRoles = sessionStorage.getItem(SESSION_STORAGE_USER_ROLES_KEY);
		return storedRoles ? JSON.parse(storedRoles) : null;
	}

	isLoggedIn(): boolean {
		const account: AccountInfo = this.msalService.instance.getAllAccounts()[0];
		const validToken: boolean = this.tokenService.isValidAccessToken();
		if (account && validToken) {
			return true;
		}
		return false;
	}

	getAccessToken() {
		return this.tokenService.getAccessToken();
	}
	getUserName(): object {
		const account = this.msalService.instance.getAllAccounts()[0];
		if (account.idTokenClaims) {
			this.idUser = account.idTokenClaims.extension_app_user_id;
			this.isSystemOperator =
				account.idTokenClaims.extension_es_operador_sistema;
		}
		const nombreCompleto = {
			nombre:
				account.idTokenClaims != null && account.idTokenClaims.given_name
					? account.idTokenClaims.given_name
					: account.name,
			apellido:
				account.idTokenClaims != null && account.idTokenClaims.family_name
					? account.idTokenClaims.family_name
					: null
		};
		return nombreCompleto;
	}

	private getUserRolesPermissions(changeCompany = false) {
		if (this.getUserRoles() == null || changeCompany == true) {
			this.user.sessionRolesPermisos().subscribe(response => {
				this.setUserRoles(response.data);
			});
		}
	}
	private setUserRoles(roles: Rol[]) {
		/** For RoleGuard */
		this.setSessionUserRoles(roles);
		/** For MenuItems */
		this.userRoles.next(roles);
		this.userRolesInicial.next(roles);
	}
	changeCompany(id: number) {
		this.companyChanging.next();
		sessionStorage.setItem(SESSION_STORAGE_CURRENT_COMPANY_KEY, id.toString());
		this.getUserRolesPermissions(true);
		this.companiesLoaded = false;
		this.getUserCompany();
		this.router.navigate(['/home']);
	}

	getUserCompany() {
		if (this.companiesLoaded) return;
		this.companiesLoaded = true;
		this.user
			.sessionEmpresa()
			.subscribe(
				(userCompanies: { data: { id: number; razonSocial: string }[] }) => {
					if (!this.isSystemOperator) {
						this.userCompanies.next(userCompanies.data);
						if (
							sessionStorage.getItem(SESSION_STORAGE_CURRENT_COMPANY_KEY) ==
							null
						) {
							sessionStorage.setItem(
								SESSION_STORAGE_CURRENT_COMPANY_KEY,
								userCompanies.data[0].id.toString()
							);
							this.userCompany.next(userCompanies.data[0]);
						} else {
							this.userCompany.next(
								userCompanies.data.filter(
									x =>
										x.id ==
										Number(
											sessionStorage.getItem(
												SESSION_STORAGE_CURRENT_COMPANY_KEY
											)
										)
								)[0]
							);
						}
					}
					this.getUserRolesPermissions();
				}
			);
	}

	deleteToken() {
		this.companyChanging.next();
		sessionStorage.clear();
	}
}
